import { setUnauthorizedHandler } from '@lms/api-client/backend';
import { useEffect } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { BrandProvider } from '@/features/branding/brand-provider';
import { AdminApp } from '@/routes/admin/admin-app';
import { LegalPage } from '@/routes/legal/legal-page';
import { NotFound } from '@/routes/not-found';
import { TokenLanding } from '@/routes/token-landing';
import { AdminLogin } from '@/routes/trainee/admin-login';
import { BrandIndex } from '@/routes/trainee/brand-index';
import { CodeLogin } from '@/routes/trainee/code-login';
import { CoursePlayer } from '@/routes/trainee/course-player';
import { CoursePreview } from '@/routes/trainee/course-preview';
import { LoginError } from '@/routes/trainee/login-error';
import { LoginSent } from '@/routes/trainee/login-sent';
import { TraineeDashboard } from '@/routes/trainee/trainee-dashboard';

// The default brand for a bare visit to "/", matching the Next app's
// `app/route.ts` permanent redirect to /safe.
const DEFAULT_BRAND = 'safe';

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to={`/${DEFAULT_BRAND}`} replace />} />
      {/* Magic-link landing: exchanges the token for a session, then redirects
          into the admin area. Must precede the /:brand/* catch-all. */}
      <Route path="/api/login/token" element={<TokenLanding />} />
      <Route path="/:brand/*" element={<BrandApp />} />
    </Routes>
  );
}

// Everything below a tenant slug: sets up branding + the 401→login handler,
// then the per-brand route tree. Replaces the Next `[brand]` segment and the
// `x-slug` header set by proxy.ts.
function BrandApp() {
  const { brand } = useParams<{ brand: string }>();
  const slug = brand ?? DEFAULT_BRAND;
  const navigate = useNavigate();

  useEffect(() => {
    setUnauthorizedHandler(() => navigate(`/${slug}/login/admin`));
    return () => setUnauthorizedHandler(undefined);
  }, [slug, navigate]);

  return (
    <BrandProvider slug={slug}>
      <Routes>
        <Route index element={<BrandIndex />} />

        {/* Trainee + admin auth */}
        <Route path="login" element={<CodeLogin />} />
        <Route path="login/admin" element={<AdminLogin />} />
        <Route path="login/sent" element={<LoginSent />} />
        <Route path="login/error" element={<LoginError />} />

        {/* Trainee course-taking */}
        <Route path="me/:id" element={<TraineeDashboard />} />
        <Route path="course/:sessionId" element={<CoursePlayer />} />
        <Route path="course-preview/:courseId" element={<CoursePreview />} />

        {/* Legal / MDX (locale in the path) */}
        <Route path="gdpr" element={<Navigate to="en" replace />} />
        <Route path="gdpr/:locale" element={<LegalPage doc="gdpr" />} />
        <Route path="privacy-policy" element={<Navigate to="en" replace />} />
        <Route
          path="privacy-policy/:locale"
          element={<LegalPage doc="privacy-policy" />}
        />
        <Route path="terms-of-service" element={<Navigate to="en" replace />} />
        <Route
          path="terms-of-service/:locale"
          element={<LegalPage doc="terms-of-service" />}
        />

        {/* Admin area (own nested routes + shell) */}
        <Route path="admin/*" element={<AdminApp />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrandProvider>
  );
}
